import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  CircularProgress,
  Button,
  alpha,
} from "@mui/material";
import {
  Restaurant as RestaurantIcon,
  PendingActions as PendingIcon,
  Today as TodayIcon,
  AttachMoney as MoneyIcon,
  LocalShipping as DeliveryIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  TrendingUp,
  AccessTime,
  CheckCircle,
  Cancel as CancelIcon,
  RestaurantMenu,
  Storefront,
  TakeoutDining,
} from "@mui/icons-material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { useTheme, useMediaQuery } from "@mui/material";
import { useOrders } from "../../hooks/useOrders";
import { useDispatch, useSelector } from "react-redux";

const RestaurantDashboard = () => {
  const { stats, loading, getStats } = useOrders();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { user } = useSelector((state) => state.user);

  useEffect(() => {
    getStats();
  }, []);

  // Status mapping for display
  const statusMap = {
    PENDING: { label: "Pending", color: "#f59e0b", icon: <AccessTime /> },
    CONFIRMED: { label: "Confirmed", color: "#3b82f6", icon: <CheckCircle /> },
    PREPARING: { label: "Preparing", color: "#8b5cf6", icon: <RestaurantIcon /> },
    COMPLETED: { label: "Completed", color: "#10b981", icon: <CheckCircle /> },
    DELIVERED: { label: "Delivered", color: "#10b981", icon: <CheckCircle /> },
    CANCELLED: { label: "Cancelled", color: "#ef4444", icon: <CancelIcon /> },
  };

  const typeMap = {
    DINE_IN: { label: "Dine In", icon: <RestaurantMenu />, color: "#3b82f6" },
    DELIVERY: { label: "Delivery", icon: <DeliveryIcon />, color: "#10b981" },
    TAKEAWAY: { label: "Takeaway", icon: <TakeoutDining />, color: "#f59e0b" },
  };

  const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#8b5cf6", "#ef4444"];

  // Generate stat cards with real data
  const statCards = [
    {
      title: "Total Orders",
      value: stats?.totalOrders || 0,
      icon: <RestaurantIcon />,
      color: "#1976d2",
      description: "All time orders",
    },
    {
      title: "Pending Orders",
      value: stats?.pendingOrders || 0,
      icon: <PendingIcon />,
      color: "#ed6c02",
      description: "Require attention",
    },
    {
      title: "Today's Orders",
      value: stats?.todayOrders || 0,
      icon: <TodayIcon />,
      color: "#2e7d32",
      description: "Orders today",
    },
    {
      title: "Total Revenue",
      value: `₹${(stats?.totalRevenue || 0).toLocaleString()}`,
      icon: <MoneyIcon />,
      color: "#9c27b0",
      description: "All time revenue",
    },
    {
      title: "Today's Revenue",
      value: `₹${(stats?.todayRevenue || 0).toLocaleString()}`,
      icon: <MoneyIcon />,
      color: "#0288d1",
      description: "Revenue today",
    },

    {
      title: "Total Refund Due",
      value: `₹${Number(stats?.totalRefundDue || 0).toFixed(2)}`,
      icon: "💸",
      color: "#c62828",
      description: "Total Refund Due",
    },
  ];

  // Prepare order type data from API
  const orderTypeData = stats?.ordersByType
    ? Object.entries(stats.ordersByType).map(([name, value]) => ({
        name: typeMap[name]?.label || name.replace('_', ' '),
        value,
        key: name,
      }))
    : [];

  // Prepare order status data from API
  const orderStatusData = stats?.ordersByStatus
    ? Object.entries(stats.ordersByStatus).map(([name, value]) => ({
        name,
        label: statusMap[name]?.label || name.charAt(0) + name.slice(1).toLowerCase(),
        value,
        color: statusMap[name]?.color || "#6b7280",
        icon: statusMap[name]?.icon || <AccessTime />,
      }))
    : [];

  // Prepare weekly revenue data from API
  const weeklyRevenueData = stats?.weeklyRevenue?.data || [];
  
  // Prepare recent orders from API
  const recentOrders = stats?.recentOrders || [];

  // Format weekly revenue for chart
  const chartData = weeklyRevenueData.length > 0 
    ? weeklyRevenueData.map(item => ({
        day: item.dayName || format(new Date(item.date), 'EEE'),
        revenue: item.revenue || 0,
      }))
    : generateFallbackWeeklyData();

  // Fallback data if API doesn't return weekly data
  function generateFallbackWeeklyData() {
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const avgDaily = Math.round((stats?.totalRevenue || 0) / 30);
    return days.map(day => ({
      day,
      revenue: Math.round(avgDaily * (0.7 + Math.random() * 0.6)),
    }));
  }

  // Helper to get status color
  const getStatusColor = (status) => {
    return statusMap[status]?.color || "#6b7280";
  };

  // Helper to get status icon
  const getStatusIcon = (status) => {
    return statusMap[status]?.icon || <AccessTime />;
  };

  // Helper to get type icon
  const getTypeIcon = (type) => {
    return typeMap[type]?.icon || <Storefront />;
  };

  // Helper to get type color
  const getTypeColor = (type) => {
    return typeMap[type]?.color || "#6b7280";
  };

  // Helper to get type label
  const getTypeLabel = (type) => {
    return typeMap[type]?.label || type?.replace('_', ' ') || 'Unknown';
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress sx={{ color: "#1976d2" }} />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: { xs: 2, md: 3 }, 
      bgcolor: "#f8fafc", 
      minHeight: "100vh",
      maxWidth: '100%',
      overflowX: 'hidden'
    }}>
      {/* Header with User Info */}
      <Paper
        sx={{
          // background:
          //   "linear-gradient(135deg, #1a237e 0%, #283593 50%, #3949ab 100%)",
          bgcolor: "var(--bg-light)",
          borderRadius: 3,
          p: 3,
          mb: 3,
          // color: "white",
          color:"var(--text)",
          maxWidth: '100%',
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item size={{xs: 12, md: 8}}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              {user?.restaurantName || "Restaurant Dashboard"}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1, flexWrap: "wrap" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <PersonIcon sx={{ fontSize: 18, opacity: 0.9 }} />
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  {user?.ownerName || "Owner"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <PhoneIcon sx={{ fontSize: 18, opacity: 0.9 }} />
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  {user?.mobile || "N/A"}
                </Typography>
              </Box>
            </Box>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Welcome back! Here's what's happening with your restaurant today.
            </Typography>
          </Grid>
          <Grid item  size={{xs: 12, md: 4}} sx={{ textAlign: { md: "right" } }}>
            <Button
              variant="contained"
              startIcon={<WhatsAppIcon />}
              sx={{
                bgcolor: "#25D366",
                color: "white",
                "&:hover": { bgcolor: "#128C7E" },
                fontWeight: 600,
                borderRadius: 2,
                px: 3,
                width: { xs: '100%', md: 'auto' }
              }}
              onClick={() =>
                window.open(`https://wa.me/${user?.whatsappNumber || ""}`, "_blank")
              }
            >
              WhatsApp Customers
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Main Stats Cards */}
      <Grid 
        container 
        spacing={3} 
        sx={{ 
          mb: 3,
          display: 'flex',
          flexWrap: 'nowrap',
          overflowX: 'auto',
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
        }}
      >
        {statCards.map((stat, index) => (
          <Grid 
            item 
            key={index}
            sx={{
              minWidth: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc(20% - 12px)' },
              flexShrink: 0,
              flexGrow: 1,
            }}
          >
            <Card
              sx={{
                height: "100%",
                borderRadius: 3,
                border: "1px solid #e2e8f0",
                boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                },
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: alpha(stat.color, 0.1),
                      color: stat.color,
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                    }}
                  >
                    {stat.icon}
                  </Avatar>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>
                  {stat.value}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#64748b", fontWeight: 600, mb: 0.5 }}
                >
                  {stat.title}
                </Typography>
                <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                  {stat.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          
        ))}
        
      </Grid>
  

      {/* Charts Section */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' },
        gap: 3,
        mb: 3,
        width: '100%'
      }}>
        {/* Weekly Revenue Chart */}
        <Box sx={{ 
          width: { xs: '100%', md: '50%' },
          flex: 1
        }}>
          <Card sx={{ 
            borderRadius: 3, 
            height: "100%",
            width: '100%'
          }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 3,
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Weekly Revenue Trend
                </Typography>
                <Chip
                  label={stats?.weeklyRevenue?.summary ? 
                    `Total: ₹${stats.weeklyRevenue.summary.total.toLocaleString()}` : 
                    "Last 7 days"}
                  size="small"
                  variant="outlined"
                  icon={<TrendingUp sx={{ fontSize: 16 }} />}
                  sx={{ fontWeight: 600 }}
                />
              </Box>
              {stats?.weeklyRevenue?.summary && (
                <Box sx={{ display: "flex", gap: 3, mb: 2, flexWrap: "wrap" }}>
                  <Typography variant="body2" sx={{ color: "#64748b" }}>
                    Average: <strong>₹{stats.weeklyRevenue.summary.average.toLocaleString()}</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#64748b" }}>
                    Change: <strong style={{ 
                      color: stats.weeklyRevenue.summary.changeDirection === 'up' ? '#10b981' : '#ef4444'
                    }}>
                      {stats.weeklyRevenue.summary.change > 0 ? '+' : ''}
                      {stats.weeklyRevenue.summary.change}%
                    </strong>
                  </Typography>
                </Box>
              )}
              <Box sx={{ width: "100%", height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke="#e2e8f0"
                    />
                    <XAxis
                      dataKey="day"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: "#64748b" }}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: "#64748b" }}
                      tickFormatter={(value) => `₹${value}`}
                    />
                    <Tooltip
                      formatter={(value) => [`₹${value}`, "Revenue"]}
                      labelStyle={{ color: "#1e293b" }}
                    />
                    <Bar
                      dataKey="revenue"
                      radius={[6, 6, 0, 0]}
                      fill="#4f46e5"
                      barSize={40}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Order Types Pie Chart */}
        <Box sx={{ 
          width: { xs: '100%', md: '50%' },
          flex: 1
        }}>
          <Card sx={{ 
            borderRadius: 3, 
            height: "100%",
            width: '100%'
          }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                Order Distribution
              </Typography>
              {orderTypeData.length > 0 ? (
                <>
                  <Box sx={{ width: "100%", height: 250, mb: 2 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={orderTypeData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) =>
                            `${name}: ${(percent * 100).toFixed(0)}%`
                          }
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {orderTypeData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={COLORS[index % COLORS.length]}
                            />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, justifyContent: "center" }}>
                    {orderTypeData.map((item, index) => (
                      <Box key={item.name} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: "50%",
                            bgcolor: COLORS[index % COLORS.length],
                          }}
                        />
                        <Typography variant="caption" sx={{ color: "#64748b" }}>
                          {item.name}: {item.value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </>
              ) : (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                    No order data available
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Recent Orders & Order Status */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', lg: 'row' },
        gap: 3,
        width: '100%'
      }}>
        {/* Recent Orders */}
        <Box sx={{ 
          width: { xs: '100%', lg: '58%' },
          flex: 1
        }}>
          <Card sx={{ 
            borderRadius: 3,
            height: "100%",
            width: '100%'
          }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 3,
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Recent Orders
                </Typography>
                <Button
                  size="small"
                  onClick={() => navigate("/owner/orders")}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  View All ({stats?.totalOrders || 0})
                </Button>
              </Box>
              {recentOrders.length > 0 ? (
                <List disablePadding>
                  {recentOrders.map((order, index) => (
                    <React.Fragment key={order.orderId || index}>
                      <ListItem
                        sx={{
                          px: 0,
                          py: 1.5,
                          "&:hover": { bgcolor: "#f8fafc" },
                          borderRadius: 1,
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              bgcolor: alpha(getStatusColor(order.status), 0.1),
                              color: getStatusColor(order.status),
                            }}
                          >
                            {getStatusIcon(order.status)}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography variant="subtitle2" fontWeight={600}>
                              {order.customerName || "Guest"}
                            </Typography>
                          }
                          secondary={
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                flexWrap: "wrap",
                                gap: 1,
                              }}
                            >
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Avatar
                                  sx={{
                                    width: 20,
                                    height: 20,
                                    bgcolor: alpha(getTypeColor(order.orderType), 0.1),
                                    color: getTypeColor(order.orderType),
                                  }}
                                >
                                  {getTypeIcon(order.orderType)}
                                </Avatar>
                                <Typography variant="caption" color="text.secondary">
                                  {getTypeLabel(order.orderType)} • {format(new Date(order.createdAt), 'hh:mm a')}
                                </Typography>
                              </Box>
                              <Chip
                                label={statusMap[order.status]?.label || order.status}
                                size="small"
                                sx={{
                                  bgcolor: alpha(getStatusColor(order.status), 0.1),
                                  color: getStatusColor(order.status),
                                  fontWeight: 600,
                                  fontSize: "0.7rem",
                                }}
                              />
                            </Box>
                          }
                        />
                        <Box sx={{ textAlign: "right", ml: 2, minWidth: 80 }}>
                          <Typography variant="subtitle1" fontWeight={700}>
                            ₹{order.totalAmount.toLocaleString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {order.itemCount} items
                          </Typography>
                        </Box>
                      </ListItem>
                      {index < recentOrders.length - 1 && (
                        <Divider sx={{ my: 0.5 }} />
                      )}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                    No recent orders
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Order Status Summary */}
        <Box sx={{ 
          width: { xs: '100%', lg: '42%' },
          flex: 1
        }}>
          <Card sx={{ 
            borderRadius: 3, 
            height: "100%",
            width: '100%'
          }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                Order Status Summary
              </Typography>
              {orderStatusData.length > 0 ? (
                <Grid container spacing={2}>
                  {orderStatusData.map(({ name, label, value, color, icon }) => (
                    <Grid item size={{xs: 6}} key={name}>
                      <Paper
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          bgcolor: alpha(color, 0.1),
                          border: `1px solid ${alpha(color, 0.3)}`,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: "50%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              bgcolor: alpha(color, 0.2),
                              color: color,
                            }}
                          >
                            {icon}
                          </Box>
                          <Box>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 700,
                                color: color,
                              }}
                            >
                              {value}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, color: "#1e293b" }}
                            >
                              {label}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                    No status data available
                  </Typography>
                </Box>
              )}
              
              {/* Order Type Summary */}
              {orderTypeData.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: "#1e293b" }}>
                    Order Types
                  </Typography>
                  <Grid container spacing={1}>
                    {orderTypeData.map(({ name, value, key }) => (
                      <Grid item xs={4} size={{xs: 4}} key={key}>
                        <Paper
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            textAlign: "center",
                            bgcolor: alpha(getTypeColor(key), 0.1),
                            border: `1px solid ${alpha(getTypeColor(key), 0.2)}`,
                          }}
                        >
                          <Avatar
                            sx={{
                              width: 30,
                              height: 30,
                              margin: "0 auto 8px",
                              bgcolor: alpha(getTypeColor(key), 0.2),
                              color: getTypeColor(key),
                            }}
                          >
                            {getTypeIcon(key)}
                          </Avatar>
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {value}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#64748b" }}>
                            {name}
                          </Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              <Box sx={{ mt: 3, pt: 2, borderTop: "1px solid #e2e8f0" }}>
                <Typography variant="body2" sx={{ color: "#64748b", mb: 1 }}>
                  Total pending orders:{" "}
                  <span style={{ fontWeight: 700, color: "#1e293b" }}>
                    {stats?.pendingOrders || 0}
                  </span>
                </Typography>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<RestaurantIcon />}
                  onClick={() => navigate("/owner/orders")}
                  sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                bgcolor: "var(--primary)",
                "&:hover": { bgcolor: "var(--primary-dark)" },
              }}
                >
                  Manage Orders
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Current Date */}
      <Typography
        variant="caption"
        sx={{
          display: "block",
          textAlign: "center",
          mt: 3,
          color: "#94a3b8",
        }}
      >
        {format(new Date(), "EEEE, MMMM dd, yyyy • hh:mm a")}
      </Typography>
    </Box>
  );
};

export default RestaurantDashboard;